import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  LinearProgress,
} from '@mui/material';
import PlayersSelector from './PlayersSelector';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

const THEMES = [
  { value: 'AVANT_ARRIERE', label: 'Avant / Arrière' },
  { value: 'LATERALES_CENTRALE', label: 'Latérales / Centrale' },
  { value: 'AMORTI', label: 'Amorti' },
  { value: 'SMATCH', label: 'Smatch' },
  { value: 'SERVICE', label: 'Service' },
];

// --- Pastel palette (UI) ---
const COLORS = {
  // Pastel colors
  green: '#BFE7C4',      // pastel green
  greenHover: '#A9DDB0',
  red: '#F7B6B6',        // pastel red
  redHover: '#F3A2A2',
  // "White" needs to be visible -> hatch pattern
  whiteBg: '#FFFFFF',
  whiteBorder: '#CFCFCF',
  // Trend bar
  trendA1: '#D7C6FF',
  trendA2: '#A994F4',
  trendB1: '#BFE6FF',
  trendB2: '#7EC8FF',
  // Neutral
  surface: '#F7F7FB',
};

function clampInt(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(1, Math.floor(n));
}

function pointsAttribution({ isWinner, reachedGreenZone }) {
  if (isWinner && reachedGreenZone) return 4;
  if (isWinner && !reachedGreenZone) return 3;
  if (!isWinner && reachedGreenZone) return 2;
  return 1;
}

// Barre "tendance": 0 => avantage total B, 50 égalité, 100 avantage total A
function matchTrendPercent(totalA, totalB, pointsToWin) {
  const win = clampInt(pointsToWin, 11);
  if (win <= 0) return 50;
  const diff = totalA - totalB;
  const raw = 50 + (diff / win) * 50;
  return Math.max(0, Math.min(100, raw));
}

// Petite barre de répartition des points: blanc/vert/rouge (visible)
// - blanc = motif hachuré + bordure
function SplitBar({ white = 0, green = 0, red = 0 }) {
  const total = (white || 0) + (green || 0) + (red || 0);
  if (!total) {
    return (
      <Box
        sx={{
          height: 14,
          borderRadius: 999,
          bgcolor: '#F0F0F0',
          border: '2px solid #E0E0E0',
        }}
      />
    );
  }

  const wPct = (white / total) * 100;
  const gPct = (green / total) * 100;
  const rPct = (red / total) * 100;

  return (
    <Box
      sx={{
        height: 14,
        borderRadius: 999,
        overflow: 'hidden',
        display: 'flex',
        border: '2px solid #D9D9D9',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        bgcolor: '#fff',
      }}
    >
      {/* White/hatch */}
      <Box
        sx={{
          width: `${wPct}%`,
          minWidth: white > 0 ? 10 : 0,
          backgroundColor: COLORS.whiteBg,
          backgroundImage:
            'repeating-linear-gradient(45deg, rgba(0,0,0,0.10) 0, rgba(0,0,0,0.10) 2px, transparent 2px, transparent 6px)',
          borderRight: white > 0 ? `1px solid ${COLORS.whiteBorder}` : 'none',
        }}
      />
      {/* Green pastel */}
      <Box
        sx={{
          width: `${gPct}%`,
          minWidth: green > 0 ? 10 : 0,
          bgcolor: COLORS.green,
          borderRight: green > 0 ? '1px solid rgba(0,0,0,0.06)' : 'none',
        }}
      />
      {/* Red pastel */}
      <Box
        sx={{
          width: `${rPct}%`,
          minWidth: red > 0 ? 10 : 0,
          bgcolor: COLORS.red,
        }}
      />
    </Box>
  );
}

export default function MatchAManiereMobile() {
  const navigate = useNavigate();

  // Setup state
  const [selectedClasse, setSelectedClasse] = useState('');
  const [playerA, setPlayerA] = useState('');
  const [playerB, setPlayerB] = useState('');
  const [theme, setTheme] = useState(THEMES[0].value);

  const [pointsToWin, setPointsToWin] = useState(11);
  const [greenTarget, setGreenTarget] = useState(6);

  const [started, setStarted] = useState(false);

  // Names for selected player IDs
  const [eleves, setEleves] = useState([]);
  const [loadingEleves, setLoadingEleves] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!selectedClasse) {
      setEleves([]);
      return () => { mounted = false; };
    }
    setLoadingEleves(true);

    api.get(`/eleves?classe=${encodeURIComponent(selectedClasse)}`)
      .then(res => {
        if (!mounted) return;
        setEleves(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => {
        console.warn('MatchAManiereMobile: impossible de charger eleves', err);
        setEleves([]);
      })
      .finally(() => {
        if (mounted) setLoadingEleves(false);
      });

    return () => { mounted = false; };
  }, [selectedClasse]);

  // FIX ESLINT: stable callback (used in useMemo deps)
  const getEleveLabelById = useCallback((id) => {
    if (!id) return '';
    const found = eleves.find(e => String(e._id) === String(id));
    if (!found) return '';
    const prenom = found.prenom || '';
    const nom = found.nom || '';
    return `${prenom} ${nom}`.trim() || (found.email || 'Élève');
  }, [eleves]);

  const playerALabel = useMemo(
    () => getEleveLabelById(playerA) || 'Joueur A',
    [playerA, getEleveLabelById]
  );
  const playerBLabel = useMemo(
    () => getEleveLabelById(playerB) || 'Joueur B',
    [playerB, getEleveLabelById]
  );

  // Match state (zone points)
  const [aGreen, setAGreen] = useState(0);
  const [aRed, setARed] = useState(0);
  const [aWhite, setAWhite] = useState(0);

  const [bGreen, setBGreen] = useState(0);
  const [bRed, setBRed] = useState(0);
  const [bWhite, setBWhite] = useState(0);

  // History for undo
  const [history, setHistory] = useState([]); // { who: 'A'|'B', kind: 'WHITE'|'GREEN'|'RED' }

  const totalA = aGreen + aRed + aWhite;
  const totalB = bGreen + bRed + bWhite;

  const winner = useMemo(() => {
    const win = clampInt(pointsToWin, 11);
    if (totalA >= win || totalB >= win) {
      if (totalA > totalB) return 'A';
      if (totalB > totalA) return 'B';
      return null;
    }
    return null;
  }, [totalA, totalB, pointsToWin]);

  const aReachedGreen = aGreen >= clampInt(greenTarget, 6);
  const bReachedGreen = bGreen >= clampInt(greenTarget, 6);
  const winnerReachedGreen = winner === 'A' ? aReachedGreen : winner === 'B' ? bReachedGreen : false;

  const scoreLabel = useMemo(() => {
    const t = THEMES.find(x => x.value === theme)?.label || theme;
    return `Match “à la manière” — thème : ${t}`;
  }, [theme]);

  const trend = useMemo(() => matchTrendPercent(totalA, totalB, pointsToWin), [totalA, totalB, pointsToWin]);

  const resetMatch = () => {
    setAGreen(0); setARed(0); setAWhite(0);
    setBGreen(0); setBRed(0); setBWhite(0);
    setHistory([]);
  };

  const endMatch = () => {
    setStarted(false);
    resetMatch();
  };

  const canStart = selectedClasse && playerA && playerB && playerA !== playerB;

  const addPoint = (who, kind) => {
    if (winner) return;

    if (who === 'A') {
      if (kind === 'WHITE') setAWhite(v => v + 1);
      if (kind === 'GREEN') setAGreen(v => v + 1);
      if (kind === 'RED') setARed(v => v + 1);
    } else {
      if (kind === 'WHITE') setBWhite(v => v + 1);
      if (kind === 'GREEN') setBGreen(v => v + 1);
      if (kind === 'RED') setBRed(v => v + 1);
    }

    setHistory(h => [...h, { who, kind }]);
  };

  const undoLastPoint = () => {
    setHistory(h => {
      if (!h.length) return h;
      const last = h[h.length - 1];

      if (last.who === 'A') {
        if (last.kind === 'WHITE') setAWhite(v => Math.max(0, v - 1));
        if (last.kind === 'GREEN') setAGreen(v => Math.max(0, v - 1));
        if (last.kind === 'RED') setARed(v => Math.max(0, v - 1));
      } else {
        if (last.kind === 'WHITE') setBWhite(v => Math.max(0, v - 1));
        if (last.kind === 'GREEN') setBGreen(v => Math.max(0, v - 1));
        if (last.kind === 'RED') setBRed(v => Math.max(0, v - 1));
      }

      return h.slice(0, -1);
    });
  };

  const renderResultsOnly = !!winner;

  return (
    <Paper sx={{ p: 2, mt: 2, maxWidth: 720, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="h6">Badminton — Match à la manière (mobile)</Typography>
        <Button variant="outlined" onClick={() => navigate('/apps/badminton')}>Retour</Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      {!started ? (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Paramètres du match
          </Typography>

          <PlayersSelector
            selectedClasse={selectedClasse}
            setSelectedClasse={setSelectedClasse}
            player1={playerA}
            setPlayer1={setPlayerA}
            player2={playerB}
            setPlayer2={setPlayerB}
          />

          {loadingEleves ? <Alert severity="info">Chargement des élèves…</Alert> : null}

          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography variant="subtitle2">Thème retenu</Typography>
            <ToggleButtonGroup
              value={theme}
              exclusive
              onChange={(e, v) => { if (v) setTheme(v); }}
              sx={{ flexWrap: 'wrap' }}
            >
              {THEMES.map(t => (
                <ToggleButton key={t.value} value={t.value} size="small" sx={{ textTransform: 'none' }}>
                  {t.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1 }}>
            <TextField
              label="Points pour gagner"
              type="number"
              value={pointsToWin}
              onChange={e => setPointsToWin(clampInt(e.target.value, 11))}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="Seuil zone verte"
              type="number"
              value={greenTarget}
              onChange={e => setGreenTarget(clampInt(e.target.value, 6))}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                variant="contained"
                disabled={!canStart}
                onClick={() => setStarted(true)}
                fullWidth
                sx={{ height: '100%' }}
              >
                Démarrer
              </Button>
            </Box>
          </Box>

          {!canStart && (
            <Alert severity="info">
              Choisis une classe et deux joueurs différents pour démarrer.
            </Alert>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 2 }}>
          {renderResultsOnly ? null : (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {scoreLabel}
              </Typography>

              {/* Trend bar - pastel */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{playerALabel}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{playerBLabel}</Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={trend}
                  sx={{
                    height: 12,
                    borderRadius: 999,
                    bgcolor: '#eee',
                    border: '1px solid #dcdcdc',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      background: trend >= 50
                        ? `linear-gradient(90deg, ${COLORS.trendA1}, ${COLORS.trendA2})`
                        : `linear-gradient(90deg, ${COLORS.trendB1}, ${COLORS.trendB2})`
                    }
                  }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                  Tendance : {trend === 50 ? 'égalité' : trend > 50 ? `avantage ${playerALabel}` : `avantage ${playerBLabel}`}
                </Typography>
              </Box>

              {/* Bandeau score */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: COLORS.surface
              }}>
                <Box>
                  <Typography sx={{ fontWeight: 800 }}>{playerALabel}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Blanc: {aWhite} — Vert: {aGreen} — Rouge: {aRed}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>
                    {totalA} : {totalB}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Objectif {clampInt(pointsToWin, 11)} pts • Zone verte à {clampInt(greenTarget, 6)}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontWeight: 800 }}>{playerBLabel}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Blanc: {bWhite} — Vert: {bGreen} — Rouge: {bRed}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button variant="outlined" onClick={undoLastPoint} disabled={!history.length}>
                  ↩️ Annuler le dernier point
                </Button>
                <Button variant="outlined" onClick={resetMatch}>
                  Réinitialiser score
                </Button>
                <Button variant="outlined" color="error" onClick={endMatch}>
                  Terminer
                </Button>
              </Box>

              {/* Zones */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {/* A */}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography sx={{ fontWeight: 900, mb: 1 }}>{playerALabel}</Typography>

                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => addPoint('A', 'WHITE')}
                      sx={{
                        bgcolor: COLORS.whiteBg,
                        color: '#111',
                        border: `2px solid ${COLORS.whiteBorder}`,
                        backgroundImage:
                          'repeating-linear-gradient(45deg, rgba(0,0,0,0.10) 0, rgba(0,0,0,0.10) 2px, transparent 2px, transparent 6px)',
                        '&:hover': { bgcolor: '#fafafa' },
                        py: 1.3,
                        fontWeight: 900
                      }}
                    >
                      +1 Point sur erreur de l'adversaire
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => addPoint('A', 'GREEN')}
                      sx={{
                        bgcolor: COLORS.green,
                        color: '#0b3d14',
                        '&:hover': { bgcolor: COLORS.greenHover },
                        py: 1.3,
                        fontWeight: 900
                      }}
                    >
                      +1 ZONE VERTE
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => addPoint('A', 'RED')}
                      sx={{
                        bgcolor: COLORS.red,
                        color: '#5b1010',
                        '&:hover': { bgcolor: COLORS.redHover },
                        py: 1.3,
                        fontWeight: 900
                      }}
                    >
                      +1 ZONE ROUGE
                    </Button>
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Zone verte : {aGreen}/{clampInt(greenTarget, 6)}
                  </Typography>
                </Paper>

                {/* B */}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography sx={{ fontWeight: 900, mb: 1 }}>{playerBLabel}</Typography>

                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => addPoint('B', 'WHITE')}
                      sx={{
                        bgcolor: COLORS.whiteBg,
                        color: '#111',
                        border: `2px solid ${COLORS.whiteBorder}`,
                        backgroundImage:
                          'repeating-linear-gradient(45deg, rgba(0,0,0,0.10) 0, rgba(0,0,0,0.10) 2px, transparent 2px, transparent 6px)',
                        '&:hover': { bgcolor: '#fafafa' },
                        py: 1.3,
                        fontWeight: 900
                      }}
                    >
                      +1 Point sur erreur de l'adversaire
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => addPoint('B', 'GREEN')}
                      sx={{
                        bgcolor: COLORS.green,
                        color: '#0b3d14',
                        '&:hover': { bgcolor: COLORS.greenHover },
                        py: 1.3,
                        fontWeight: 900
                      }}
                    >
                      +1 ZONE VERTE
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => addPoint('B', 'RED')}
                      sx={{
                        bgcolor: COLORS.red,
                        color: '#5b1010',
                        '&:hover': { bgcolor: COLORS.redHover },
                        py: 1.3,
                        fontWeight: 900
                      }}
                    >
                      +1 ZONE ROUGE
                    </Button>
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Zone verte : {bGreen}/{clampInt(greenTarget, 6)}
                  </Typography>
                </Paper>
              </Box>
            </>
          )}

          {/* === Résultats (page seule) === */}
          {winner ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>
                Résultats — {scoreLabel}
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {winner === 'A' ? playerALabel : playerBLabel}
                </Typography>

                <Typography sx={{ mt: 0.5, fontWeight: 800 }}>
                  {winnerReachedGreen ? 'Victoire AVEC la manière' : 'Victoire SANS la manière'}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Score final : {totalA} - {totalB}
                </Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 900, textAlign: 'center', mb: 1 }}>
                Répartition des types de points
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                <Paper sx={{ p: 1.5, textAlign: 'center' }} elevation={0}>
                  <Typography sx={{ fontWeight: 900 }}>{playerALabel}</Typography>
                  <SplitBar white={aWhite} green={aGreen} red={aRed} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Blanc: {aWhite} • Vert: {aGreen} • Rouge: {aRed}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 1.5, textAlign: 'center' }} elevation={0}>
                  <Typography sx={{ fontWeight: 900 }}>{playerBLabel}</Typography>
                  <SplitBar white={bWhite} green={bGreen} red={bRed} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Blanc: {bWhite} • Vert: {bGreen} • Rouge: {bRed}
                  </Typography>
                </Paper>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 900, textAlign: 'center' }}>
                Attribution des points
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1 }}>
                <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#F3FBF4' }} elevation={0}>
                  <Typography sx={{ fontWeight: 900 }}>{playerALabel}</Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 900 }}>
                    {pointsAttribution({ isWinner: winner === 'A', reachedGreenZone: aReachedGreen })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {winner === 'A' ? 'Gagnant' : 'Perdant'} — {aReachedGreen ? 'zone verte' : 'zone rouge'}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#FFF3F3' }} elevation={0}>
                  <Typography sx={{ fontWeight: 900 }}>{playerBLabel}</Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 900 }}>
                    {pointsAttribution({ isWinner: winner === 'B', reachedGreenZone: bReachedGreen })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {winner === 'B' ? 'Gagnant' : 'Perdant'} — {bReachedGreen ? 'zone verte' : 'zone rouge'}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, justifyContent: 'center' }}>
                <Button variant="contained" onClick={endMatch}>
                  Nouveau match
                </Button>
                <Button variant="outlined" onClick={() => navigate('/apps/badminton')}>
                  Retour menu badminton
                </Button>
              </Box>
            </Paper>
          ) : null}
        </Box>
      )}
    </Paper>
  );
}